import Application from "../../Application";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import CreateIndexTypeCategoryInteractor from "../interactors/CreateIndexTypeCategoryInteractor";
import GetAllIndexTypeCategoriesInteractor from "../interactors/GetAllIndexTypeCategoriesInteractor";
import UpdateIndexTypeCategoryInteractor from "../interactors/UpdateIndexTypeCategoryInteractor";
import { IIndexTypeCategory } from "../model/IndexTypeCategory";
import FindConnectionCandidatesForCategoryInteractor from "../interactors/FindConnectionCandidatesForCategoryInteractor";
import ConnectIndexTypeCategoriesInteractor from "../interactors/ConnectIndexTypeCategoriesInteractor";

export interface TIndexTypeCategoryPresenter extends TLoadingAwarePresenter {
  indexTypeCategories: IIndexTypeCategory[];
  createModalVisible?: boolean;
  categoryCandidates: IIndexTypeCategory[];
}
export interface IIndexTypeCategoryPresenter
  extends TIndexTypeCategoryPresenter,
    TPresentable {
  loadAllIndexTypeCategories(indexTypeCategories: IIndexTypeCategory[]): void;
  createIndexTypeCategory(
    indexTypeCategory: IIndexTypeCategory
  ): Promise<boolean>;
  showIndexTypeCategoryDetails(id: number): void;
  updateIndexTypeCategory(indexTypeCategory: IIndexTypeCategory): void;
  findConnectionCandidates(category: IIndexTypeCategory): void;
  toggleCandidate(categoryCandidate: IIndexTypeCategory): void;
  showBulkEditor(id: number): void;
}
const defaultState: TIndexTypeCategoryPresenter = {
  indexTypeCategories: [],
  createModalVisible: false,
  categoryCandidates: []
};
const IndexTypeCategoryPresenter = withStore<
  IIndexTypeCategoryPresenter,
  TIndexTypeCategoryPresenter
>(({ application, store, loader, translate }): IIndexTypeCategoryPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TIndexTypeCategoryPresenter>();

  loader.start("indexTypeCategoryLoader");
  const loadAllIndexTypeCategories = (
    indexTypeCategories: IIndexTypeCategory[]
  ) => {
    _store.update({
      indexTypeCategories
    });
    loader.stop("indexTypeCategoryLoader");
  };
  const getAllIndexTypeCategories = async () => {
    let indexTypeCategories = await _application.container
      .resolve<GetAllIndexTypeCategoriesInteractor>("getAllIndexTypeCategories")
      .execute();
    loadAllIndexTypeCategories(indexTypeCategories);
  };

  const createIndexTypeCategory = async (
    indexTypeCategory: IIndexTypeCategory
  ) => {
    loader.start("indexTypeCategoryLoader");
    await _application.container
      .resolve<CreateIndexTypeCategoryInteractor>("createIndexTypeCategory")
      .execute(indexTypeCategory);
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.index_type.create_success_message"));
    loader.stop("indexTypeCategoryLoader");
    await getAllIndexTypeCategories();
    return true;
  };

  const updateIndexTypeCategory = async (
    indexTypeCategory: IIndexTypeCategory
  ) => {
    const { categoryCandidates } =
      _store.getState<TIndexTypeCategoryPresenter>();
    loader.start("indexTypeCategoryLoader");
    await _application.container
      .resolve<UpdateIndexTypeCategoryInteractor>("updateIndexTypeCategory")
      .execute(indexTypeCategory);
    let selectedCandidates = categoryCandidates.filter(
      (candidate) => candidate.connected === true
    );
    await _application.container
      .resolve<ConnectIndexTypeCategoriesInteractor>(
        "connectIndexTypeCategories"
      )
      .execute(indexTypeCategory.id, selectedCandidates);
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.index_type.create_success_message"));
    loader.stop("indexTypeCategoryLoader");
    await getAllIndexTypeCategories();
  };

  const showIndexTypeCategoryDetails = (id: number) => {
    return _application.navigator.push({
      pathname: `/index-type-categories/${id.toString()}/groups`
    });
  };

  const findConnectionCandidates = async (category: IIndexTypeCategory) => {
    let categoryCandidates = await _application.container
      .resolve<FindConnectionCandidatesForCategoryInteractor>(
        "findConnectionCandidatesForCategory"
      )
      .execute(category.id);
    _store.update({
      categoryCandidates
    });
  };

  const toggleCandidate = (categoryCandidate: IIndexTypeCategory) => {
    const { categoryCandidates } =
      _store.getState<TIndexTypeCategoryPresenter>();
    categoryCandidates.forEach((candidate) => {
      if (candidate.id === categoryCandidate.id) {
        candidate.connected = !candidate.connected;
      }
    });
    console.log(categoryCandidates);
    _store.update({ categoryCandidates });
  };

  const showBulkEditor = (id: number) => {
    return _application.navigator.push({
      pathname: "/category-editor/" + id.toString()
    });
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    loadAllIndexTypeCategories,
    showIndexTypeCategoryDetails,
    createIndexTypeCategory,
    updateIndexTypeCategory,
    findConnectionCandidates,
    toggleCandidate,
    showBulkEditor
  };
}, defaultState);
export default IndexTypeCategoryPresenter;
